import CoreBase from '../../../services/engines/CoreBase';
import journalService from '../../../services/JournalService';
import responseHelper from '../../../services/ResponseHelper';
import prioUpdater from '../../../components/shared/PrioUpdater';
import entryUpdater from '../../../pages/DashboardComponents/TemplateEngine/InputFormRenderer/EntryUpdater';
import { BOARD_STATE_ACTIVE, DEFAULT_BOARD_LANES, DEFAULT_ISSUE_PRIO, ISSUE_BODY_TEMPLATE, NEW_BOARD } from './boardConst';

// All board writes: lane config (Board-note body), new Issues, stage/title changes, item body
class BoardSaver extends CoreBase {
    _name = 'BoardSaver';

    /**
     * @param {{raw: object, lanes: object[]}} board
     * @returns {Promise<boolean>} true on success
     */
    saveLanes = async (board) => {
        try {
            const data = { ...board.raw, body: JSON.stringify({ lanes: board.lanes }, null, 2) };
            const resp = await journalService.createOrUpdate({ data });
            return responseHelper.check(resp, '', `${this._name}.saveLanes`) >= 0;
        } catch (error) {
            this.logError('saveLanes', error.message);
            return false;
        }
    };

    /**
     * Creates a new board with the default lane set — arrives via OnJournalUpdated.
     * @param {string} title
     * @param {string} service
     * @returns {Promise<string|null>} rk of the new board, null on failure
     */
    createBoard = async (title, service) => {
        try {
            const data = { ...NEW_BOARD, rk: '', title, service, body: JSON.stringify({ lanes: DEFAULT_BOARD_LANES }, null, 2) };
            const resp = await journalService.createOrUpdate({ data });
            if (responseHelper.check(resp, '', `${this._name}.createBoard`) < 0) return null;
            return resp.data.rk;
        } catch (error) {
            this.logError('createBoard', error.message);
            return null;
        }
    };

    /**
     * Creates an Issue on the board — the card arrives via OnJournalUpdated, no local insert.
     * @param {{rk: string, raw: object}} board
     * @param {string} stage - dropStage of the target lane
     * @param {string} title
     * @param {string} cat
     * @returns {Promise<boolean>} true on success
     */
    createIssue = async (board, stage, title, cat) => {
        try {
            const data = {
                rk: '',
                title,
                body: ISSUE_BODY_TEMPLATE,
                type: 'Issue',
                cat,
                service: board.raw.service,
                stage,
                stateId: BOARD_STATE_ACTIVE,
                parentRK: board.rk,
                prioId: DEFAULT_ISSUE_PRIO,
                importanceId: DEFAULT_ISSUE_PRIO,
            };
            const resp = await journalService.createOrUpdate({ data });
            return responseHelper.check(resp, '', `${this._name}.createIssue`) >= 0;
        } catch (error) {
            this.logError('createIssue', error.message);
            return false;
        }
    };

    /**
     * Posts a stage change via user/prio — item must carry all fields PrioUpdater sends.
     * @param {object} item
     * @param {string} stage
     * @returns {Promise<void>}
     */
    saveStage = (item, stage) => prioUpdater.save({ ...item, stage });

    /**
     * @param {object} item - board item
     * @param {object} fields - fields to overwrite
     * @returns {Promise<void>}
     */
    saveFields = (item, fields) => prioUpdater.save({ ...item, ...fields });

    /**
     * @param {object} item - board item
     * @param {string} title
     * @returns {Promise<void>}
     */
    saveTitle = (item, title) => prioUpdater.save({ ...item, title });

    /**
     * Body-only update — other note fields are untouched.
     * @param {object} item - board item
     * @param {string} body
     * @returns {Promise<boolean>} true on success
     */
    saveBody = (item, body) => entryUpdater.savePlainTextBody(item, body);
}

const boardSaver = new BoardSaver();
export default boardSaver;
